/**
 * Pending destination — the in-app page a signed-out visitor was trying to
 * reach, held across the trip through the gym selector's login modal.
 *
 * The recap and admin-report e-mails link straight at
 * `/profile#settings/emails` (the unsubscribe row). A reader who is not signed
 * in on that device used to be bounced to `/` with `replace`, which threw the
 * destination away entirely: no prompt, no explanation, and nothing to go back
 * to — they landed on the gym list and had to guess that the setting they came
 * for lives four taps deep in a profile they are not signed in to.
 *
 * So the destination is stashed here first. The gym selector opens its login
 * modal because something is waiting, and sends the reader there once auth
 * resolves. Same shape as `sendly-pending-gym-slug`, which does this for a gym
 * card tapped while logged out — this one just carries a whole in-app path.
 *
 * localStorage, not sessionStorage: a Google/Apple sign-in leaves the origin
 * and comes back, and iOS Safari does not always hand the same session storage
 * back to the returning page.
 */
import { localStore } from "./safeStorage";

const KEY = "sendly-pending-path";

/** Anything older than this is a leftover, not an intention. A stale entry
 *  would pop the login modal open on an ordinary visit to the gym list, which
 *  is worse than the lost deep link it was meant to save. */
const MAX_AGE_MS = 30 * 60 * 1000;

/**
 * True for a same-origin in-app path (`/profile#settings/emails`). Rejects
 * `//evil.com` and `/\evil.com`, both of which browsers read as protocol-
 * relative URLs — the value survives in localStorage where anything on the
 * origin can write it, so it is validated on the way in AND on the way out.
 */
function isInAppPath(path) {
  return typeof path === "string" && path.startsWith("/") && !/^\/[/\\]/.test(path);
}

/** Remember where the visitor was heading. Ignores anything but an in-app path. */
export function setPendingDestination(path) {
  if (!isInAppPath(path)) return;
  localStore.setJSON(KEY, { path, at: Date.now() });
}

/** The waiting destination, or null. Leaves it in place — see `takePendingDestination`. */
export function peekPendingDestination() {
  const raw = localStore.get(KEY);
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw);
    if (!isInAppPath(path) || !at || Date.now() - at > MAX_AGE_MS) {
      localStore.remove(KEY);
      return null;
    }
    return path;
  } catch { return null; }
}

/** The waiting destination, cleared on the way out so it can only be used once. */
export function takePendingDestination() {
  const path = peekPendingDestination();
  clearPendingDestination();
  return path;
}

/** Forget it — the visitor dismissed the login prompt instead of signing in. */
export function clearPendingDestination() {
  localStore.remove(KEY);
}
